import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { inr, imgUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Building2, Calendar, IndianRupee, BedDouble, BadgePercent, TrendingUp, Plus, Crown, MessageSquare } from "lucide-react";

function Stat({ icon: Icon, label, value, color = "#FF385C", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <p className="text-xs text-[#717171] uppercase tracking-wide">{label}</p>
      <p className="font-display font-bold text-2xl mt-1">{value}</p>
    </div>
  );
}

export default function HostDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, h, sb] = await Promise.all([
        api.get("/host/stats").then((r) => r.data),
        api.get("/host/hotels").then((r) => r.data),
        api.get("/host/subscription").then((r) => r.data).catch(() => null),
      ]);
      setStats(s);
      setHotels(h);
      setSub(sb);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {sub && (sub.status === "trial" || sub.status === "expired") && (
          <Link to="/host/subscription" data-testid="subscription-banner" className="block mb-6">
            <div className={`rounded-2xl p-5 flex items-center gap-4 ${sub.status === "expired" ? "bg-[#C13515]/10 border border-[#C13515]/30" : "bg-gradient-to-r from-[#FFF2F4] to-[#FFF8E5] border border-[#FFE0E6]"}`}>
              <Crown className={`w-6 h-6 ${sub.status === "expired" ? "text-[#C13515]" : "text-[#FF385C]"} shrink-0`} />
              <div className="flex-1">
                <p className="font-display font-semibold">
                  {sub.status === "trial"
                    ? `You're on your free trial — ${sub.trial_days_left} day${sub.trial_days_left === 1 ? "" : "s"} left`
                    : "Your subscription expired — reactivate to see new inquiries"}
                </p>
                <p className="text-sm text-[#555]">{sub.status === "trial" ? `After the trial, ${inr(sub.monthly_fee)}/month auto-renews via Razorpay.` : `Subscribe to ${inr(sub.monthly_fee)}/mo to keep receiving direct bookings.`}</p>
              </div>
              <Button className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white shrink-0" data-testid="subscription-cta-btn">
                {sub.status === "expired" ? "Reactivate" : "Subscribe now"}
              </Button>
            </div>
          </Link>
        )}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold">Hi {user?.name?.split(" ")[0]}</h1>
            <p className="text-[#717171] mt-1">Welcome to your zero-commission dashboard</p>
          </div>
          <div className="flex gap-2">
            <Link to="/host/inquiries">
              <Button data-testid="view-inquiries-btn" variant="outline" className="rounded-xl">
                <MessageSquare className="w-4 h-4 mr-1" /> Inquiries
              </Button>
            </Link>
            <Link to="/host/properties/new">
              <Button data-testid="add-property-btn" className="rounded-xl bg-[#222] hover:bg-black text-white">
                <Plus className="w-4 h-4 mr-1" /> Add property
              </Button>
            </Link>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            <Stat testid="stat-revenue" icon={IndianRupee} label="Revenue" value={inr(stats.revenue)} color="#FF385C" />
            <Stat testid="stat-commission-saved" icon={BadgePercent} label="Saved (vs 18% OTA)" value={inr(stats.commission_saved)} color="#008A05" />
            <Stat testid="stat-bookings" icon={Calendar} label="Total bookings" value={stats.total_bookings} color="#008489" />
            <Stat testid="stat-hotels" icon={Building2} label="Properties" value={stats.hotels} color="#222" />
            <Stat testid="stat-rooms" icon={BedDouble} label="Rooms" value={stats.rooms} color="#FFB400" />
          </div>
        )}

        <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-[#EBEBEB] flex items-center justify-between">
            <h2 className="font-display font-semibold text-xl">Your properties</h2>
            <Link to="/host/properties" className="text-sm text-[#FF385C] font-semibold">View all →</Link>
          </div>
          {hotels.length === 0 ? (
            <div className="p-12 text-center">
              <TrendingUp className="w-12 h-12 text-[#717171] mx-auto mb-3" />
              <p className="text-[#717171] mb-4">No properties yet. Add your first one to start earning — commission free.</p>
              <Link to="/host/properties/new">
                <Button className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">Add property</Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-[#EBEBEB]">
              {hotels.slice(0, 5).map((h) => (
                <Link key={h.id} to={`/host/properties/${h.id}`} className="flex items-center gap-4 p-4 hover:bg-[#F7F7F9] transition-colors">
                  <img src={h.images?.[0] ? imgUrl(h.images[0]) : "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300"} className="w-20 h-16 object-cover rounded-lg" alt="" />
                  <div className="flex-1">
                    <p className="font-semibold">{h.name}</p>
                    <p className="text-sm text-[#717171]">{h.city}, {h.country}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="font-semibold">{h.min_price ? inr(h.min_price) : "—"}</p>
                    <p className="text-xs text-[#717171]">starting / night</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
